import React, { useState, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  X, 
  Plus, 
  GripVertical, 
  Upload, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  Play,
  FileText,
  Loader2,
  Save
} from 'lucide-react';
import { uploadApi, coursesApi } from '../services/api';
import './CourseBuilder.css';

const CourseBuilder = ({ course, onSave, onClose }) => {
  const [courseData, setCourseData] = useState(course || {
    title: '',
    description: '',
    instructor: 'Admin',
    price: 0,
    thumbnail_url: '',
    sections: []
  });
  
  const [expandedSections, setExpandedSections] = useState({});
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  const videoInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Add new section
  const addSection = () => {
    const newSection = {
      id: generateId(),
      title: `Section ${courseData.sections.length + 1}`,
      episodes: [],
      order: courseData.sections.length
    };
    setCourseData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setExpandedSections(prev => ({
      ...prev,
      [newSection.id]: true
    }));
  };

  // Update section title
  const updateSectionTitle = (sectionId, title) => {
    setCourseData(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, title } : s
      )
    }));
  };

  // Delete section
  const deleteSection = (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section and all its episodes?')) {
      setCourseData(prev => ({
        ...prev,
        sections: prev.sections.filter(s => s.id !== sectionId)
      }));
    }
  };

  // Add new episode to section
  const addEpisode = (sectionId) => {
    const section = courseData.sections.find(s => s.id === sectionId);
    const newEpisode = {
      id: generateId(),
      title: `Episode ${(section?.episodes?.length || 0) + 1}`,
      description: '',
      video_url: '',
      video_file_id: null,
      video_file_name: '',
      attachments: [],
      order: section?.episodes?.length || 0
    };
    
    setCourseData(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId 
          ? { ...s, episodes: [...(s.episodes || []), newEpisode] }
          : s
      )
    }));
  };

  // Update episode
  const updateEpisode = (sectionId, episodeId, updates) => {
    setCourseData(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId 
          ? { 
              ...s, 
              episodes: s.episodes.map(e => 
                e.id === episodeId ? { ...e, ...updates } : e
              )
            }
          : s
      )
    }));
  };

  // Delete episode
  const deleteEpisode = (sectionId, episodeId) => {
    if (window.confirm('Are you sure you want to delete this episode?')) {
      setCourseData(prev => ({
        ...prev,
        sections: prev.sections.map(s => 
          s.id === sectionId 
            ? { ...s, episodes: s.episodes.filter(e => e.id !== episodeId) }
            : s
        )
      }));
      if (editingEpisode?.id === episodeId) {
        setEditingEpisode(null);
      }
    }
  };

  // Handle video upload for episode
  const handleVideoUpload = async (event, sectionId, episodeId) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid video file (MP4, WebM, OGG, or MOV)');
      return;
    }
    if (file.size > 1024 * 1024 * 1024) {
      alert('Video file must be less than 1GB');
      return;
    }

    setIsUploading(true);
    setUploadProgress('Starting upload...');

    try {
      const uploadResult = await uploadApi.uploadVideo(file, (percent) => {
        setUploadProgress(`Uploading video... ${percent}%`);
      });
      const videoUrl = uploadResult.url.startsWith('http') 
        ? uploadResult.url 
        : uploadApi.getFileUrl(uploadResult.url);
      updateEpisode(sectionId, episodeId, {
        video_file_id: uploadResult.id,
        video_file_name: file.name,
        video_url: videoUrl
      });
      setUploadProgress('Video uploaded successfully!');
      setTimeout(() => setUploadProgress(''), 2000);
    } catch (error) {
      console.error('Video upload failed:', error);
      alert(`Failed to upload video: ${error?.message || error}\n\nCheck the browser console (F12) for details.`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle attachment upload for episode
  const handleAttachmentUpload = async (event, sectionId, episodeId) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(`Uploading ${files.length} file(s)...`);

    try {
      const uploadedFiles = [];
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          alert(`File "${file.name}" is too large. Maximum size is 20MB.`);
          continue;
        }
        const uploadResult = await uploadApi.uploadFile(file);
        const fileUrl = uploadResult.url.startsWith('http') 
          ? uploadResult.url 
          : uploadApi.getFileUrl(uploadResult.url);
        uploadedFiles.push({
          id: uploadResult.id,
          name: file.name,
          file_type: file.type,
          size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
          file_id: uploadResult.id,
          url: fileUrl
        });
      }

      const episode = courseData.sections
        .find(s => s.id === sectionId)
        ?.episodes.find(e => e.id === episodeId);
      
      if (episode) {
        updateEpisode(sectionId, episodeId, {
          attachments: [...(episode.attachments || []), ...uploadedFiles]
        });
      }
      
      setUploadProgress('Files uploaded!');
      setTimeout(() => setUploadProgress(''), 2000);
    } catch (error) {
      console.error('Attachment upload failed:', error);
      alert('Failed to upload file(s). Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Remove attachment from episode
  const removeAttachment = (sectionId, episodeId, attachmentId) => {
    const episode = courseData.sections
      .find(s => s.id === sectionId)
      ?.episodes.find(e => e.id === episodeId);
    
    if (episode) {
      updateEpisode(sectionId, episodeId, {
        attachments: episode.attachments.filter(a => a.id !== attachmentId)
      });
    }
  };

  // Handle thumbnail upload
  const handleThumbnailUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPG, PNG, GIF, or WEBP)');
      return;
    }

    setIsUploading(true);
    setUploadProgress('Uploading thumbnail...');

    try {
      const uploadResult = await uploadApi.uploadImage(file);
      // Use the URL directly - it's already a complete URL (S3 or local)
      const thumbnailUrl = uploadResult.url.startsWith('http') 
        ? uploadResult.url 
        : uploadApi.getFileUrl(uploadResult.url);
      setCourseData(prev => ({
        ...prev,
        thumbnail_url: thumbnailUrl
      }));
      setUploadProgress('Thumbnail uploaded!');
      setTimeout(() => setUploadProgress(''), 2000);
    } catch (error) {
      console.error('Thumbnail upload failed:', error);
      alert(`Failed to upload thumbnail: ${error?.message || error}\n\nCheck the browser console (F12) for details.`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle drag and drop
  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'section') {
      const newSections = Array.from(courseData.sections);
      const [removed] = newSections.splice(source.index, 1);
      newSections.splice(destination.index, 0, removed);
      
      // Update order
      newSections.forEach((s, i) => s.order = i);
      
      setCourseData(prev => ({ ...prev, sections: newSections }));
    } else if (type === 'episode') {
      const sectionId = source.droppableId.replace('episodes-', '');
      const section = courseData.sections.find(s => s.id === sectionId);
      
      if (section) {
        const newEpisodes = Array.from(section.episodes);
        const [removed] = newEpisodes.splice(source.index, 1);
        newEpisodes.splice(destination.index, 0, removed);
        
        // Update order
        newEpisodes.forEach((e, i) => e.order = i);
        
        setCourseData(prev => ({
          ...prev,
          sections: prev.sections.map(s => 
            s.id === sectionId ? { ...s, episodes: newEpisodes } : s
          )
        }));
      }
    }
  };

  // Save course
  const handleSave = async () => {
    if (!courseData.title.trim()) {
      alert('Please enter a course title');
      return;
    }

    setIsSaving(true);
    try {
      if (courseData.id) {
        await coursesApi.update(courseData.id, courseData);
      } else {
        const created = await coursesApi.create(courseData);
        setCourseData(prev => ({ ...prev, id: created.id }));
      }
      onSave(courseData);
    } catch (error) {
      console.error('Failed to save course:', error);
      alert('Failed to save course. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="course-builder-overlay">
      <div className="course-builder-modal">
        <div className="course-builder-header">
          <h2>Course Builder</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="course-builder-content">
          {/* Course Details */}
          <div className="course-details-section">
            <h3>Course Details</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Course Title *</label>
                <input
                  type="text"
                  value={courseData.title}
                  onChange={(e) => setCourseData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Forex Trading Mastery"
                />
              </div>
              
              <div className="form-group">
                <label>Instructor</label>
                <input
                  type="text"
                  value={courseData.instructor}
                  onChange={(e) => setCourseData(prev => ({ ...prev, instructor: e.target.value }))}
                  placeholder="Instructor name"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={courseData.description}
                onChange={(e) => setCourseData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter your course description...

You can use:
• Bullet points (start line with - or •)
• Numbered lists (start line with 1. or 2.)
• Line breaks for paragraphs"
                rows={6}
                className="description-textarea"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price ($)</label>
                <input
                  type="number"
                  value={courseData.price}
                  onChange={(e) => setCourseData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label>Thumbnail</label>
                <div className="thumbnail-upload">
                  {courseData.thumbnail_url ? (
                    <div className="thumbnail-preview">
                      <img src={courseData.thumbnail_url} alt="Thumbnail" />
                      <button onClick={() => setCourseData(prev => ({ ...prev, thumbnail_url: '' }))}>
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <button 
                      className="upload-btn" 
                      onClick={() => thumbnailInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Upload size={18} />
                      Upload Thumbnail
                    </button>
                  )}
                  <input
                    type="file"
                    ref={thumbnailInputRef}
                    onChange={handleThumbnailUpload}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="upload-progress">
              {isUploading && <Loader2 size={16} className="spinner" />}
              <span>{uploadProgress}</span>
            </div>
          )}

          {/* Sections & Episodes */}
          <div className="sections-container">
            <div className="sections-header">
              <h3>Course Content</h3>
              <button className="add-section-btn" onClick={addSection}>
                <Plus size={18} />
                Add Section
              </button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sections" type="section">
                {(provided) => (
                  <div 
                    className="sections-list"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {courseData.sections.map((section, sectionIndex) => (
                      <Draggable 
                        key={section.id} 
                        draggableId={section.id} 
                        index={sectionIndex}
                      >
                        {(provided, snapshot) => (
                          <div
                            className={`section-item ${snapshot.isDragging ? 'dragging' : ''}`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <div className="section-header">
                              <div className="section-header-left">
                                <span 
                                  className="drag-handle"
                                  {...provided.dragHandleProps}
                                >
                                  <GripVertical size={18} />
                                </span>
                                <button 
                                  className="expand-btn"
                                  onClick={() => toggleSection(section.id)}
                                >
                                  {expandedSections[section.id] ? (
                                    <ChevronDown size={18} />
                                  ) : (
                                    <ChevronRight size={18} />
                                  )}
                                </button>
                                <input
                                  type="text"
                                  className="section-title-input"
                                  value={section.title}
                                  onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                                  placeholder="Section title"
                                />
                                <span className="episode-count">
                                  {section.episodes?.length || 0} episodes
                                </span>
                              </div>
                              <button 
                                className="delete-btn"
                                onClick={() => deleteSection(section.id)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>

                            {expandedSections[section.id] && (
                              <div className="section-content">
                                <Droppable 
                                  droppableId={`episodes-${section.id}`} 
                                  type="episode"
                                >
                                  {(provided) => (
                                    <div 
                                      className="episodes-list"
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                    >
                                      {section.episodes?.map((episode, episodeIndex) => (
                                        <Draggable
                                          key={episode.id}
                                          draggableId={episode.id}
                                          index={episodeIndex}
                                        >
                                          {(provided, snapshot) => (
                                            <div
                                              className={`episode-item ${snapshot.isDragging ? 'dragging' : ''} ${editingEpisode?.id === episode.id ? 'editing' : ''}`}
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                            >
                                              <div className="episode-header">
                                                <span 
                                                  className="drag-handle"
                                                  {...provided.dragHandleProps}
                                                >
                                                  <GripVertical size={16} />
                                                </span>
                                                <span className="episode-number">
                                                  {episodeIndex + 1}.
                                                </span>
                                                <input
                                                  type="text"
                                                  className="episode-title-input"
                                                  value={episode.title}
                                                  onChange={(e) => updateEpisode(section.id, episode.id, { title: e.target.value })}
                                                  placeholder="Episode title"
                                                />
                                                <div className="episode-badges">
                                                  {episode.video_url && (
                                                    <span className="badge video-badge">
                                                      <Play size={12} /> Video
                                                    </span>
                                                  )}
                                                  {episode.attachments?.length > 0 && (
                                                    <span className="badge attachment-badge">
                                                      <FileText size={12} /> {episode.attachments.length}
                                                    </span>
                                                  )}
                                                </div>
                                                <button 
                                                  className="edit-btn"
                                                  onClick={() => setEditingEpisode(
                                                    editingEpisode?.id === episode.id 
                                                      ? null 
                                                      : { ...episode, sectionId: section.id }
                                                  )}
                                                >
                                                  {editingEpisode?.id === episode.id ? 'Close' : 'Edit'}
                                                </button>
                                                <button 
                                                  className="delete-btn"
                                                  onClick={() => deleteEpisode(section.id, episode.id)}
                                                >
                                                  <Trash2 size={14} />
                                                </button>
                                              </div>

                                              {editingEpisode?.id === episode.id && (
                                                <div className="episode-details">
                                                  <div className="form-group">
                                                    <label>Description</label>
                                                    <textarea
                                                      value={episode.description || ''}
                                                      onChange={(e) => updateEpisode(section.id, episode.id, { description: e.target.value })}
                                                      placeholder="Describe this episode...

Use bullet points (- or •) and numbered lists for formatting."
                                                      rows={4}
                                                      className="description-textarea"
                                                    />
                                                  </div>

                                                  <div className="form-group">
                                                    <label>Video</label>
                                                    {episode.video_url ? (
                                                      <div className="video-preview-container">
                                                        <div className="video-preview">
                                                          <Play size={18} />
                                                          <span>{episode.video_file_name || 'Video uploaded'}</span>
                                                          <button 
                                                            className="remove-video-btn"
                                                            onClick={() => updateEpisode(section.id, episode.id, { video_url: '', video_file_id: null, video_file_name: '' })}
                                                            title="Remove video"
                                                          >
                                                            <X size={14} />
                                                          </button>
                                                        </div>
                                                        <button 
                                                          className="change-video-btn"
                                                          onClick={() => {
                                                            videoInputRef.current.dataset.sectionId = section.id;
                                                            videoInputRef.current.dataset.episodeId = episode.id;
                                                            videoInputRef.current.click();
                                                          }}
                                                          disabled={isUploading}
                                                        >
                                                          <Upload size={14} />
                                                          Change Video
                                                        </button>
                                                      </div>
                                                    ) : (
                                                      <div className="upload-actions">
                                                        <input
                                                          type="text"
                                                          placeholder="Video URL (YouTube, Vimeo, etc.)"
                                                          value={episode.video_url || ''}
                                                          onChange={(e) => updateEpisode(section.id, episode.id, { video_url: e.target.value })}
                                                        />
                                                        <span className="or-text">or</span>
                                                        <button 
                                                          className="upload-btn"
                                                          onClick={() => {
                                                            videoInputRef.current.dataset.sectionId = section.id;
                                                            videoInputRef.current.dataset.episodeId = episode.id;
                                                            videoInputRef.current.click();
                                                          }}
                                                          disabled={isUploading}
                                                        >
                                                          <Upload size={16} />
                                                          Upload Video
                                                        </button>
                                                      </div>
                                                    )}
                                                  </div>

                                                  <div className="form-group">
                                                    <label>Attachments</label>
                                                    {episode.attachments?.length > 0 && (
                                                      <div className="attachments-list">
                                                        {episode.attachments.map(att => (
                                                          <div key={att.id} className="attachment-item">
                                                            <FileText size={14} />
                                                            <span>{att.name}</span>
                                                            <span className="size">{att.size}</span>
                                                            <button onClick={() => removeAttachment(section.id, episode.id, att.id)}>
                                                              <X size={12} />
                                                            </button>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                    <button 
                                                      className="upload-btn"
                                                      onClick={() => {
                                                        attachmentInputRef.current.dataset.sectionId = section.id;
                                                        attachmentInputRef.current.dataset.episodeId = episode.id;
                                                        attachmentInputRef.current.click();
                                                      }}
                                                      disabled={isUploading}
                                                    >
                                                      <Plus size={16} />
                                                      Add Attachments
                                                    </button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>

                                <button 
                                  className="add-episode-btn"
                                  onClick={() => addEpisode(section.id)}
                                >
                                  <Plus size={16} />
                                  Add Episode
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {courseData.sections.length === 0 && (
              <div className="empty-sections">
                <p>No sections yet. Click "Add Section" to start building your course.</p>
              </div>
            )}
          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={videoInputRef}
          onChange={(e) => {
            const sectionId = e.target.dataset.sectionId;
            const episodeId = e.target.dataset.episodeId;
            handleVideoUpload(e, sectionId, episodeId);
          }}
          accept="video/*"
          style={{ display: 'none' }}
        />
        <input
          type="file"
          ref={attachmentInputRef}
          onChange={(e) => {
            const sectionId = e.target.dataset.sectionId;
            const episodeId = e.target.dataset.episodeId;
            handleAttachmentUpload(e, sectionId, episodeId);
          }}
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip"
          multiple
          style={{ display: 'none' }}
        />

        {/* Footer */}
        <div className="course-builder-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="save-btn" 
            onClick={handleSave}
            disabled={isSaving || isUploading}
          >
            {isSaving ? (
              <>
                <Loader2 size={18} className="spinner" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Course
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseBuilder;
